import React from 'react';

import { ActionIcon, Box, Grid, Group, Image, Text, useMantineTheme } from '@mantine/core';
import { IconBrandDiscord, IconBrandInstagram, IconBrandTiktok } from '@tabler/icons-react';

import { useMediaQuery } from '@mantine/hooks';
import { getRuntimeEnvironment } from '@trappedmc/docker-frontend-runtime-env';

const { REACT_APP_FULL_URL = '', GITHUB_SHA = '', BUILD_NUMBER = '' } = getRuntimeEnvironment();

export const Footer: React.FC = () => {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);

  return (
    <Box
      style={{
        backgroundColor: 'rgba(10, 10, 10, 0.7)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '40px 24px 24px 24px'
      }}
    >
      <Grid gutter={32} mb={32}>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Box>
            <Group gap={12} mb={12}>
              <Box>
                <Text size='md' fw={600} c='white' mb={4}>
                  TrappedMC
                </Text>
                <Text size='13px' c='rgba(255, 255, 255, 0.5)'>
                  Not affiliated with Mojang.
                </Text>
              </Box>
            </Group>
            <Group gap={8}>
              <ActionIcon
                variant='subtle'
                size='lg'
                component='a'
                href='https://discord.gg/trappedmc'
                target='_blank'
                rel='noopener noreferrer'
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  transition: 'all 0.2s'
                }}
              >
                <IconBrandDiscord size={20} />
              </ActionIcon>
              <ActionIcon
                variant='subtle'
                size='lg'
                component='a'
                href='https://tiktok.com/@trapped_mc'
                target='_blank'
                rel='noopener noreferrer'
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  transition: 'all 0.2s'
                }}
              >
                <IconBrandTiktok size={20} />
              </ActionIcon>
              <ActionIcon
                variant='subtle'
                size='lg'
                component='a'
                href='https://www.instagram.com/trappedmc'
                target='_blank'
                rel='noopener noreferrer'
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  transition: 'all 0.2s'
                }}
              >
                <IconBrandInstagram size={20} />
              </ActionIcon>
            </Group>
            <Text size='10px' c='rgba(255, 255, 255, 0.3)' mt={8} style={{ fontFamily: 'monospace' }}>
              {GITHUB_SHA ? GITHUB_SHA.substring(0, 7) : '0000000'}
              &nbsp;({ BUILD_NUMBER || '0' })
            </Text>
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Box>
            <Text size='md' fw={600} c='white' mb={8}>
              Refund Policy
            </Text>
            <Text size='13px' c='rgba(255, 255, 255, 0.7)' style={{ lineHeight: 1.6 }}>
              Item damaged, defective, or not as described? Contact us within 30 days of delivery and we'll make it
              right. EU and UK customers keep their 14-day right of withdrawal, and statutory rights are unaffected.
            </Text>
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Box>
            <Text size='md' fw={600} c='white' mb={8}>
              Support
            </Text>
            <Text size='13px' c='rgba(255, 255, 255, 0.7)' style={{ lineHeight: 1.6 }}>
              For assistance or inquiries, please contact our support team via Discord by opening a ticket or by email
              at support@trappedmc.com. We're here to help ensure you have the best experience possible on TrappedMC.
            </Text>
          </Box>
        </Grid.Col>
      </Grid>
      <Box
        style={{
          borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          paddingTop: 24
        }}
      >
        <Box
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '16px' : '0',
            marginBottom: 24
          }}
        >
          <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Image
              src={`${REACT_APP_FULL_URL}/assets/images/fourthwall.svg`}
              alt='Fourthwall'
              style={{ width: 80, height: 'auto', opacity: 0.6, flexShrink: 0 }}
            />
            <Text size='12px' c='rgba(255, 255, 255, 0.5)' style={{ lineHeight: 1.5, maxWidth: isMobile ? '100%' : 400 }}>
              Our checkout process is powered by Fourthwall, ensuring secure transactions and data protection.
            </Text>
          </Box>
          <Box style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
            <Text
              component='a'
              href='https://fourthwall.com/legal/terms-of-service'
              target='_blank'
              rel='noopener noreferrer'
              size='12px'
              c='rgba(255, 255, 255, 0.6)'
              style={{ cursor: 'pointer', transition: 'color 0.2s', textDecoration: 'none' }}
            >
              Terms & Conditions
            </Text>
            <Text
              component='a'
              href='https://fourthwall.com/legal/privacy-policy'
              target='_blank'
              rel='noopener noreferrer'
              size='12px'
              c='rgba(255, 255, 255, 0.6)'
              style={{ cursor: 'pointer', transition: 'color 0.2s', textDecoration: 'none' }}
            >
              Privacy Policy
            </Text>
            <Text
              component='a'
              href='#'
              role='button'
              onClick={(e) => {
                e.preventDefault();
                window.dispatchEvent(new Event('show-cookie-consent'));
              }}
              size='12px'
              c='rgba(255, 255, 255, 0.6)'
              style={{ cursor: 'pointer', transition: 'color 0.2s', textDecoration: 'none' }}
            >
              Cookie Settings
            </Text>
          </Box>
        </Box>
        <Box
          style={{
            textAlign: isMobile ? 'left' : 'right',
            paddingTop: 16,
            marginBottom: isMobile ? 16 : 0,
          }}
        >
          <Text size='12px' c='rgba(255, 255, 255, 0.4)'>
            © {new Date().getFullYear()} TrappedMC. All rights reserved.
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
